import React, { useState, useEffect, useRef } from 'react';
import { useI18n } from '../i18n';

const AudioTranscriptionAnimation = () => {
  const { t } = useI18n();
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const timeoutRef = useRef(null);

  // Darija (Arabic script) + French phrases
  const phrases = [
    {
      segments: [
        { text: "Donc ", lang: "fr" },
        { text: "دابا ", lang: "ar" },
        { text: "كندير ", lang: "ar" },
        { text: "les sous-titres ", lang: "fr" },
        { text: "ديال الفيديو ", lang: "ar" },
        { text: "automatiquement", lang: "fr" },
      ]
    },
    {
      segments: [
        { text: "بغيت نترجم ", lang: "ar" },
        { text: "les vidéos ", lang: "fr" },
        { text: "ديالي ", lang: "ar" },
        { text: "en darija", lang: "fr" },
      ]
    },
    {
      segments: [
        { text: "C'est bon ", lang: "fr" },
        { text: "ça marche ", lang: "fr" },
        { text: "مزيان ", lang: "ar" },
        { text: "avec le format SRT ", lang: "fr" },
        { text: "ولا ", lang: "ar" },
        { text: "VTT", lang: "fr" },
      ]
    },
    {
      segments: [
        { text: "واه هادشي ", lang: "ar" },
        { text: "rapide ", lang: "fr" },
        { text: "بزاف", lang: "ar" },
        { text: ", les sous-titres ", lang: "fr" },
        { text: "كيخرجو ", lang: "ar" },
        { text: "nickel", lang: "fr" },
      ]
    },
  ];

  // Build full text from segments
  const getFullText = (phraseIndex) => {
    return phrases[phraseIndex].segments.map(s => s.text).join('');
  };

  // Audio waveform bars - simple uniform set
  const barCount = 20;

  useEffect(() => {
    const fullText = getFullText(currentPhrase);
    let index = 0;
    setDisplayedText('');
    setIsTyping(true);

    const typingInterval = setInterval(() => {
      if (index < fullText.length) {
        setDisplayedText(fullText.substring(0, index + 1));
        index++;
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);

        timeoutRef.current = setTimeout(() => {
          setCurrentPhrase((prev) => (prev + 1) % phrases.length);
        }, 2200);
      }
    }, 65);

    return () => {
      clearInterval(typingInterval);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [currentPhrase]);

  // Render text with colored segments
  const renderColoredText = () => {
    const segments = phrases[currentPhrase].segments;
    let charCount = 0;
    const result = [];

    for (let s = 0; s < segments.length; s++) {
      const seg = segments[s];
      const segStart = charCount;
      charCount += seg.text.length;

      if (segStart >= displayedText.length) break;

      const visiblePart = seg.text.substring(0, displayedText.length - segStart);

      const colorClass =
        seg.lang === 'ar' ? 'text-[#f73140]' : 'text-white';

      result.push(
        <span key={s} className={colorClass}>
          {visiblePart}
        </span>
      );
    }

    return result;
  };

  return (
    <div className="w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-5 relative overflow-hidden shadow-2xl">
      {/* Background glow */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[200px] h-[200px] bg-[#f73140] rounded-full blur-[100px] opacity-15" />
      </div>

      <div className="relative z-10 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{t('Audio')}</span>
          </div>
          <span className="text-[10px] font-medium text-slate-500">Français + دارجة</span>
        </div>

        {/* Simple audio waveform bars */}
        <div className="flex items-end justify-center gap-[4px] h-12">
          {Array.from({ length: barCount }).map((_, i) => (
            <div
              key={i}
              className="w-[3px] rounded-full bg-[#f73140]"
              style={{
                height: isTyping ? undefined : '4px',
                opacity: isTyping ? 0.7 : 0.2,
                animation: isTyping
                  ? `simplebar 0.8s ease-in-out infinite ${i * 0.06}s`
                  : 'none',
              }}
            />
          ))}
        </div>

        {/* Transcribed text display */}
        <div className="min-h-[56px] bg-black/30 rounded-xl px-4 py-3 border border-slate-700/30">
          <p className="font-medium text-sm leading-relaxed" dir="auto">
            {renderColoredText()}
            {isTyping && (
              <span className="inline-block w-[2px] h-4 bg-[#f73140] ms-0.5 animate-pulse align-middle" />
            )}
          </p>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-4 text-[10px] pt-1">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#f73140]" />
            <span className="text-slate-500">دارجة</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-white" />
            <span className="text-slate-500">Français</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes simplebar {
          0%, 100% { height: 6px; }
          50% { height: 28px; }
        }
      `}</style>
    </div>
  );
};

export default AudioTranscriptionAnimation;
